import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import styles from './Textarea.module.scss';
import cx from 'classnames';
import { Text } from 'components/Text/Text';
import { getInputRowsCount } from 'utils';

type Props = {
  labelText: string;
  inputName: string;
  errorMsg?: string;
  register(fieldName: string, registerOptions?: { [key: string]: any }): any;
  registerOptions?: { [key: string]: any };
  rowsValue?: number;
  currentValue?: string | number;
  characterCounter?: boolean;
  isForLinks?: boolean;
  height?: number;
  setValue?: UseFormSetValue<any>;
} & React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;

export const Textarea = ({
  labelText,
  inputName,
  errorMsg,
  register,
  registerOptions,
  rowsValue = 1,
  currentValue,
  characterCounter = false,
  isForLinks = true,
  height = 100,
  setValue,
  ...props
}: Props) => {
  const [rows] = useState(rowsValue);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { ref, ...rest } = register(inputName, registerOptions);

  const currentValueLength =
    currentValue === undefined
      ? 0
      : typeof currentValue === 'number'
        ? String(currentValue).length
        : currentValue.length;

  useEffect(() => {
    if (inputRef?.current && isForLinks) {
      const textareaRows = getInputRowsCount(inputRef.current.value);
      inputRef.current.rows = textareaRows;
    } else if (inputRef?.current && !isForLinks) {
      inputRef.current.style.height = '0px';
      const inputScrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = Math.min(inputScrollHeight, height) + 'px';
    }
  }, []);

  useEffect(() => {
    if (props?.maxLength && currentValue && String(currentValue).length > props?.maxLength) {
      const newValue = String(currentValue).slice(0, props.maxLength);
      setValue && setValue(inputName, newValue);
    }
  }, [currentValue]);

  const handleTextareaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (isForLinks) {
      const textareaRows = getInputRowsCount(event.target.value);
      event.target.rows = textareaRows;
      setValue && setValue(inputName, event.target.value);
    } else if (!isForLinks) {
      event.target.style.height = '0px';
      const inputScrollHeight = event.target.scrollHeight;
      event.target.style.height = Math.min(inputScrollHeight, height) + 'px';
      setValue && setValue(inputName, event.target.value);
    }
  };

  return (
    <div className={cx(styles.inputItem, { [styles.isDisabled]: props.disabled })}>
      <label htmlFor={inputName}>
        <Text type="caption">{labelText}</Text>
      </label>
      <div className={styles.inputContainer}>
        <textarea
          rows={rows}
          id={inputName}
          className={styles.textarea}
          {...rest}
          ref={(e) => {
            ref(e);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-expect-error
            inputRef.current = e;
          }}
          onChange={handleTextareaChange}
          {...props}
        />
      </div>
      {!characterCounter && (
        <div className={styles.inputItemInfo}>
          <Text type="caption" className={styles.error}>
            {errorMsg}
          </Text>
        </div>
      )}
      {characterCounter && props.maxLength && (
        <div className={styles.inputItemInfo}>
          <Text type="caption" className={styles.error}>
            {errorMsg}
          </Text>
          <Text type="caption" className={styles.counter}>
            {currentValueLength}/{props.maxLength} characters
          </Text>
        </div>
      )}
    </div>
  );
};
